import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { MaintenancePlansSection, SafetyChecklistSection } from '../components/equipmentSafety/EquipmentSafetySections';
import { equipmentSafetyKey, getEquipmentSafety } from '../services/equipmentSafetyService';

/**
 * Technik-Sicherheit (Reef Guard #1828): the safety checklist for the failures
 * software cannot see (heater stuck on, ATO / doser running on, power outage)
 * and the maintenance plans that turn into recurring equipment tasks. Loading
 * and errors are handled once here for both sections (one query).
 */
export function EquipmentSafetyPage() {
  const { t } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const query = useQuery({
    queryKey: equipmentSafetyKey(aquariumId),
    queryFn: () => getEquipmentSafety(aquariumId),
    enabled: Boolean(aquariumId),
  });

  let body;
  if (query.isPending) {
    body = <LiveRegion busy>{t('equipmentSafety.loading')}</LiveRegion>;
  } else if (query.isError) {
    body = <LoadErrorState title={t('equipmentSafety.loadError')} onRetry={() => void query.refetch()} retryLabel={t('equipmentSafety.retry')} />;
  } else {
    body = (
      <>
        <SafetyChecklistSection aquariumId={aquariumId} safety={query.data} />
        <MaintenancePlansSection aquariumId={aquariumId} safety={query.data} />
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader title={t('equipmentSafety.title')} description={t('equipmentSafety.subtitle')} />
          {body}
        </PageContent>
      }
    />
  );
}

export default EquipmentSafetyPage;
